import React, { useState } from 'react';
import { useTerminal, TERMINAL_TYPES, TERMINAL_STATUS } from '../context/TerminalContext';
import WaiterSidebar from '../components/WaiterSidebar';
import { useWaiterAuth } from '../context/WaiterAuthContext';
import './TerminalManagement.css';

const TerminalManagement = () => {
  const { currentWaiter } = useWaiterAuth();
  const {
    terminals,
    currentTerminal,
    loading,
    createTerminal,
    updateTerminal,
    deleteTerminal,
    selectTerminal,
    updateTerminalStatus
  } = useTerminal();

  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: TERMINAL_TYPES.BILLING,
    location: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await createTerminal(formData);

    if (result.success) {
      alert(`Terminal created successfully! Terminal Number: ${result.terminalNumber}`);
      setShowAddModal(false);
      setFormData({
        name: '',
        type: TERMINAL_TYPES.BILLING,
        location: '',
        description: ''
      });
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleStatusChange = async (terminalId, newStatus) => {
    const result = await updateTerminalStatus(terminalId, newStatus);
    if (result.success) {
      console.log('Terminal status updated');
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleDelete = async (terminalId) => {
    if (window.confirm('Are you sure you want to delete this terminal?')) {
      const result = await deleteTerminal(terminalId);
      if (result.success) {
        alert('Terminal deleted successfully');
      } else {
        alert(`Error: ${result.error}`);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      [TERMINAL_STATUS.ACTIVE]: '#10b981',
      [TERMINAL_STATUS.INACTIVE]: '#6b7280',
      [TERMINAL_STATUS.OFFLINE]: '#ef4444',
      [TERMINAL_STATUS.MAINTENANCE]: '#f59e0b'
    };
    return colors[status] || '#6b7280';
  };

  const getTypeIcon = (type) => {
    const icons = {
      [TERMINAL_TYPES.BILLING]: '💳',
      [TERMINAL_TYPES.KOT]: '🍽️',
      [TERMINAL_TYPES.MASTER]: '👑',
      [TERMINAL_TYPES.CAPTAIN]: '👨‍✈️'
    };
    return icons[type] || '🖥️';
  };

  const getTypeLabel = (type) => {
    const labels = {
      [TERMINAL_TYPES.BILLING]: 'Billing Counter',
      [TERMINAL_TYPES.KOT]: 'KOT Printer',
      [TERMINAL_TYPES.MASTER]: 'Master Station',
      [TERMINAL_TYPES.CAPTAIN]: 'Captain Station'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="terminal-loading">
        <div className="loading-spinner"></div>
        <p>Loading terminals...</p>
      </div>
    );
  }

  return (
    <div className="terminal-management-container">
      <WaiterSidebar currentWaiter={currentWaiter} />

      <div className="terminal-main-content">
        <div className="terminal-header">
          <div className="header-left">
            <h1>🖥️ Multi-Terminal Billing</h1>
            <p>Manage billing counters and sync across stations</p>
          </div>
          <button className="btn-add-terminal" onClick={() => setShowAddModal(true)}>
            ➕ Add New Terminal
          </button>
        </div>

        {/* Current Terminal Display */}
        {currentTerminal && (
          <div className="current-terminal-card">
            <div className="current-badge">CURRENT TERMINAL</div>
            <div className="current-terminal-info">
              <span className="terminal-icon">{getTypeIcon(currentTerminal.type)}</span>
              <div className="terminal-details">
                <h3>{currentTerminal.name}</h3>
                <p>{currentTerminal.terminalNumber} • {getTypeLabel(currentTerminal.type)}</p>
                <p className="terminal-location">📍 {currentTerminal.location}</p>
              </div>
              <div
                className="status-indicator"
                style={{ background: getStatusColor(currentTerminal.status) }}
              >
                {currentTerminal.status}
              </div>
            </div>
          </div>
        )}

        {/* Terminals Grid */}
        <div className="terminals-grid">
          {terminals.map(terminal => (
            <div
              key={terminal.id}
              className={`terminal-card ${currentTerminal?.id === terminal.id ? 'active' : ''}`}
              onClick={() => selectTerminal(terminal.id)}
            >
              <div className="terminal-card-header">
                <span className="terminal-icon-large">{getTypeIcon(terminal.type)}</span>
                <div className="terminal-number">{terminal.terminalNumber}</div>
              </div>

              <div className="terminal-card-body">
                <h3>{terminal.name}</h3>
                <p className="terminal-type">{getTypeLabel(terminal.type)}</p>
                <p className="terminal-location">📍 {terminal.location}</p>
                {terminal.description && (
                  <p className="terminal-description">{terminal.description}</p>
                )}
              </div>

              <div className="terminal-card-footer">
                <div
                  className="status-badge"
                  style={{ background: getStatusColor(terminal.status) }}
                >
                  {terminal.status}
                </div>

                <div className="terminal-actions">
                  <select
                    value={terminal.status}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleStatusChange(terminal.id, e.target.value);
                    }}
                    className="status-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value={TERMINAL_STATUS.ACTIVE}>Active</option>
                    <option value={TERMINAL_STATUS.INACTIVE}>Inactive</option>
                    <option value={TERMINAL_STATUS.OFFLINE}>Offline</option>
                    <option value={TERMINAL_STATUS.MAINTENANCE}>Maintenance</option>
                  </select>

                  <button
                    className="btn-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(terminal.id);
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {terminal.lastActive && (
                <div className="last-active">
                  Last active: {new Date(terminal.lastActive.toDate()).toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>

        {terminals.length === 0 && (
          <div className="no-terminals">
            <h3>No terminals configured</h3>
            <p>Click "Add New Terminal" to create your first billing counter</p>
          </div>
        )}

        {/* Info Panel */}
        <div className="terminal-info-panel">
          <h3>📡 Multi-Terminal Sync</h3>
          <div className="info-grid">
            <div className="info-card">
              <div className="info-number">{terminals.length}</div>
              <div className="info-label">Total Terminals</div>
            </div>
            <div className="info-card">
              <div className="info-number">
                {terminals.filter(t => t.status === TERMINAL_STATUS.ACTIVE).length}
              </div>
              <div className="info-label">Active Now</div>
            </div>
            <div className="info-card">
              <div className="info-number">
                {terminals.filter(t => t.type === TERMINAL_TYPES.BILLING).length}
              </div>
              <div className="info-label">Billing Counters</div>
            </div>
            <div className="info-card">
              <div className="info-number">
                {terminals.filter(t => t.type === TERMINAL_TYPES.MASTER).length}
              </div>
              <div className="info-label">Master Stations</div>
            </div>
          </div>

          <div className="sync-features">
            <h4>✨ Features</h4>
            <ul>
              <li>✅ Real-time order sync across all terminals</li>
              <li>✅ Master station controls all billing counters</li>
              <li>✅ KOT automatically sent to kitchen</li>
              <li>✅ Captains can settle bills from any station</li>
              <li>✅ Offline mode with auto-sync</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Terminal Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Terminal</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="terminal-form">
              <div className="form-group">
                <label>Terminal Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Billing Counter 1"
                  required
                />
              </div>

              <div className="form-group">
                <label>Terminal Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  required
                >
                  <option value={TERMINAL_TYPES.BILLING}>💳 Billing Counter</option>
                  <option value={TERMINAL_TYPES.KOT}>🍽️ KOT Printer</option>
                  <option value={TERMINAL_TYPES.MASTER}>👑 Master Station</option>
                  <option value={TERMINAL_TYPES.CAPTAIN}>👨‍✈️ Captain Station</option>
                </select>
              </div>

              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Ground Floor, Near Entrance"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Additional notes about this terminal..."
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Create Terminal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalManagement;
